import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { MSG, NAME } from '../shared';
import { useEmployee } from './employee';
import { useSocket } from './socket';

function UseProvideSite() {
    const { socket } = useSocket();
    const { employee } = useEmployee();

    const [lists, setLists] = useState([]);
    const [listsLoaded, setListsLoaded] = useState(false);

    const getServiceTypesIndividual = useCallback(() => {
        return lists.filter(
            (x) =>
                x.listType === NAME.listType.serviceTypeIndividual ||
                x.listType === NAME.listType.serviceTypeJ2TC
        );
    }, [lists]);

    const getServiceTypesIndividualCount = useCallback(() => {
        return getServiceTypesIndividual().length;
    }, [getServiceTypesIndividual]);

    const getServiceTypesGroup = useCallback(() => {
        return lists.filter((x) => x.listType === NAME.listType.serviceTypeGroup);
    }, [lists]);

    const getServiceTypesGroupCount = useCallback(() => {
        return getServiceTypesGroup().length;
    }, [getServiceTypesGroup]);

    const isValidItemName = useCallback(
        (listType, needle) => {
            const sNeedle = needle.trim().toLowerCase();

            return (
                lists.filter(
                    (l) => l.listType === listType && l.itemName.trim().toLowerCase() === sNeedle
                ).length === 0
            );
        },
        [lists]
    );

    const isValidStateAbbr = useCallback(
        (needle) => {
            const sNeedle = needle.trim().toLowerCase();

            return (
                lists.filter(
                    (l) =>
                        l.listType === NAME.listType.state &&
                        l.itemData.abbr.trim().toLowerCase() === sNeedle
                ).length === 0
            );
        },
        [lists]
    );

    const createListItem = useCallback(
        (data, cb) => {
            socket.emit(MSG.create.list, data, cb);
        },
        [socket]
    );

    const updateListItem = useCallback(
        (data, cb) => {
            socket.emit(MSG.update.list, data, cb);
        },
        [socket]
    );

    const getParents = useCallback(
        (data, cb) => {
            socket.emit(MSG.read.parents, data, cb);
        },
        [socket]
    );

    const getParents01Report = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.parents.zero1, data, cb);
        },
        [socket]
    );

    const getParents02Report = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.parents.zero2, data, cb);
        },
        [socket]
    );

    const createParent = useCallback(
        (data, cb) => {
            socket.emit(MSG.create.parent, data, cb);
        },
        [socket]
    );

    const updateParent = useCallback(
        (data, cb) => {
            socket.emit(MSG.update.parent, data, cb);
        },
        [socket]
    );

    const updateLists = (response) => {
        setLists(response.data.lists);
    };

    const getPrograms = useCallback(
        (data, cb) => {
            socket.emit(MSG.read.programs, data, cb);
        },
        [socket]
    );

    const createProgram = useCallback(
        (data, cb) => {
            socket.emit(MSG.create.program, data, cb);
        },
        [socket]
    );

    const updateProgram = useCallback(
        (data, cb) => {
            socket.emit(MSG.update.program, data, cb);
        },
        [socket]
    );

    const getServicesIndividual = useCallback(
        (data, cb) => {
            socket.emit(MSG.read.services.individual, data, cb);
        },
        [socket]
    );

    const createServiceIndividual = useCallback(
        (data, cb) => {
            socket.emit(MSG.create.service.individual, data, cb);
        },
        [socket]
    );

    const updateServiceIndividual = useCallback(
        (data, cb) => {
            socket.emit(MSG.update.service.individual, data, cb);
        },
        [socket]
    );

    const getServicesProgram = useCallback(
        (data, cb) => {
            socket.emit(MSG.read.services.program, data, cb);
        },
        [socket]
    );

    const createServiceProgram = useCallback(
        (data, cb) => {
            socket.emit(MSG.create.service.program, data, cb);
        },
        [socket]
    );

    const updateServiceProgram = useCallback(
        (data, cb) => {
            socket.emit(MSG.update.service.program, data, cb);
        },
        [socket]
    );

    const getServicesGroup = useCallback(
        (data, cb) => {
            socket.emit(MSG.read.services.group, data, cb);
        },
        [socket]
    );

    const createServiceGroup = useCallback(
        (data, cb) => {
            socket.emit(MSG.create.service.group, data, cb);
        },
        [socket]
    );

    const updateServiceGroup = useCallback(
        (data, cb) => {
            socket.emit(MSG.update.service.group, data, cb);
        },
        [socket]
    );

    const searchParents = useCallback(
        (data, cb) => {
            socket.emit(MSG.search.parents, data, cb);
        },
        [socket]
    );

    const searchPrograms = useCallback(
        (data, cb) => {
            socket.emit(MSG.search.programs, data, cb);
        },
        [socket]
    );

    const countParentsAddedBy = useCallback(
        (data, cb) => {
            socket.emit(MSG.count.parentsAddedBy, data, cb);
        },
        [socket]
    );

    const isServiceTypeJ2TC = useCallback(
        (serviceTypeId) => {
            return lists.some(
                (l) => l.listType === NAME.listType.serviceTypeJ2TC && l._id === serviceTypeId
            );
        },
        [lists]
    );

    const getChildrenByParent = useCallback(
        (data, cb) => {
            socket.emit(MSG.read.children.byParent, data, cb);
        },
        [socket]
    );

    const readOneParent = useCallback(
        (data, cb) => {
            socket.emit(MSG.readOne.parent, data, cb);
        },
        [socket]
    );

    const countAll = useCallback(
        (data, cb) => {
            socket.emit(MSG.count.all, data, cb);
        },
        [socket]
    );

    const getParentsNoEmailReport = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.parents.noEmail, data, cb);
        },
        [socket]
    );

    const getChildrenInactiveReport = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.children.inactive, data, cb);
        },
        [socket]
    );

    const getChildrenActiveOverAge12Report = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.children.activeOverAge12, data, cb);
        },
        [socket]
    );

    const getStateReport1 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.one, data, cb);
        },
        [socket]
    );

    const getStateReport2 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.two, data, cb);
        },
        [socket]
    );

    const getStateReport3 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.three, data, cb);
        },
        [socket]
    );

    const getStateReport4 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.four, data, cb);
        },
        [socket]
    );

    const getStateReport5 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.five, data, cb);
        },
        [socket]
    );

    const getStateReport6 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.six, data, cb);
        },
        [socket]
    );

    const getStateReport7 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.seven, data, cb);
        },
        [socket]
    );

    const getStateReport8 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.eight, data, cb);
        },
        [socket]
    );

    const getStateReport9 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.nine, data, cb);
        },
        [socket]
    );

    const getStateReport10 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.ten, data, cb);
        },
        [socket]
    );

    const getStateReport11 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.eleven, data, cb);
        },
        [socket]
    );

    const getStateReport12 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.twelve, data, cb);
        },
        [socket]
    );

    const getStateReport13 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.thirteen, data, cb);
        },
        [socket]
    );

    const getStateReport14 = useCallback(
        (data, cb) => {
            socket.emit(MSG.report.state.fourteen, data, cb);
        },
        [socket]
    );

    // Get Lists
    useEffect(() => {
        if (listsLoaded || !socket || !employee) {
            return;
        }

        socket.emit(MSG.read.lists, {}, (response) => {
            setLists(response.data.lists);
            setListsLoaded(true);
        });
    }, [employee, listsLoaded, socket]);

    // Listen for updates
    useEffect(() => {
        if (!socket) {
            return;
        }

        if (!employee) {
            return;
        }

        socket.on(MSG.create.list, updateLists);
        socket.on(MSG.update.list, updateLists);

        return () => {
            socket.off(MSG.create.list, updateLists);
            socket.off(MSG.update.list, updateLists);
        };
    }, [employee, socket]);

    return {
        lists,
        GetServiceTypesIndividual: getServiceTypesIndividual,
        GetServiceTypesGroup: getServiceTypesGroup,
        GetServiceTypesIndividualCount: getServiceTypesIndividualCount,
        GetServiceTypesGroupCount: getServiceTypesGroupCount,
        IsServiceTypeJ2TC: isServiceTypeJ2TC,
        CountAll: countAll,
        CountParentsAddedBy: countParentsAddedBy,
        CreateListItem: createListItem,
        CreateParent: createParent,
        CreateProgram: createProgram,
        CreateServiceGroup: createServiceGroup,
        CreateServiceIndividual: createServiceIndividual,
        CreateServiceProgram: createServiceProgram,
        GetChildrenActiveOverAge12Report: getChildrenActiveOverAge12Report,
        GetChildrenByParent: getChildrenByParent,
        GetChildrenInactiveReport: getChildrenInactiveReport,
        GetParents: getParents,
        GetParents01Report: getParents01Report,
        GetParents02Report: getParents02Report,
        GetPrograms: getPrograms,
        GetParentsNoEmailReport: getParentsNoEmailReport,
        GetServicesGroup: getServicesGroup,
        GetServicesIndividual: getServicesIndividual,
        GetServicesProgram: getServicesProgram,
        GetStateReport1: getStateReport1,
        GetStateReport2: getStateReport2,
        GetStateReport3: getStateReport3,
        GetStateReport4: getStateReport4,
        GetStateReport5: getStateReport5,
        GetStateReport6: getStateReport6,
        GetStateReport7: getStateReport7,
        GetStateReport8: getStateReport8,
        GetStateReport9: getStateReport9,
        GetStateReport10: getStateReport10,
        GetStateReport11: getStateReport11,
        GetStateReport12: getStateReport12,
        GetStateReport13: getStateReport13,
        GetStateReport14: getStateReport14,
        IsValidItemName: isValidItemName,
        IsValidStateAbbr: isValidStateAbbr,
        ReadOneParent: readOneParent,
        SearchParents: searchParents,
        SearchPrograms: searchPrograms,
        UpdateListItem: updateListItem,
        UpdateParent: updateParent,
        UpdateProgram: updateProgram,
        UpdateServiceGroup: updateServiceGroup,
        UpdateServiceIndividual: updateServiceIndividual,
        UpdateServiceProgram: updateServiceProgram
    };
}

function ProvideSite({ children }) {
    const useProvideSite = UseProvideSite();

    return <SiteContext.Provider value={useProvideSite}>{children}</SiteContext.Provider>;
}

function UseSite() {
    return useContext(SiteContext);
}

const SiteContext = createContext();

export { UseSite as useSite };

export default ProvideSite;
